

import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/solid";
import { TooltipCircle } from "components/tooltipCircle";
import { DatePickerFilterOption } from "interfaces";
import React from "react";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { abbreviateNumbers, formatNumber } from "utills/helpers"

interface CardProps {
  name: string;
  hint?: string;
  value: number | null;
  change: number | "Infinity";
  showGrowthRates: boolean;
  unit?: string;
  icon: {
    svg: any;
    fill: string;
    background: string;
  }
  redirectUrl?: string;
  dateOption: DatePickerFilterOption;
  dayDiff: number;
}

export const DataCard = ({ name, hint, unit, icon, value, change, redirectUrl, dateOption, dayDiff = 0, showGrowthRates}: CardProps) => {

  const getGrowthRateText = () => {
    switch (dateOption) {
      case DatePickerFilterOption.TODAY: return "Yesterday";
      case DatePickerFilterOption.YESTERDAY: return "The previous day";
      case DatePickerFilterOption.SEVENDAYS: return "The previous 7 days";
      case DatePickerFilterOption.THIRTYDAYS: return "The previous 30 days";
      case DatePickerFilterOption.DAYS: return `${dayDiff} day(s) prior`;
      case DatePickerFilterOption.WEEK: return "previous week";
      case DatePickerFilterOption.MONTH: return "previous month";
      case DatePickerFilterOption.QUARTER: return "previous quarter";
      case DatePickerFilterOption.YEAR: return "previous year";
      default: return "";
    }
  }
  
  return (
    <div className="overflow-hidden h-full rounded-lg bg-white py-4 shadow sm:py-6">
      <div className="px-4 sm:px-6">
        <div className="flex flex-col gap-y-2 sm:flex-row sm:items-start sm:gap-x-4">
          <div className="w-fit sm:shrink-0 rounded-full p-3" style={{backgroundColor: icon.background}}>
            <icon.svg className="h-6 w-6 text-white" fill={icon.fill} aria-hidden="true" />
          </div>
          <div>
            <div className="flex gap-x-0.5">
              <p className="truncate text-sm font-medium text-gray-500">{name}</p>
              {hint ? <TooltipCircle text={hint} /> : <></>}
            </div>
            <div className="flex flex-col gap-y-2">
              <div className="text-xl font-semibold text-gray-900 sm:text-2xl">
                <span
                  data-tooltip-id="tooltip"
                  data-tooltip-content={formatNumber(value ?? 0)}
                >
                  {unit} {value !== null ? abbreviateNumbers(value) : <div className="inline-block h-4 bg-gray-200 rounded-full w-16" />}
                </span>
              </div>
              {showGrowthRates ? (
                <React.Fragment>
                  {change !== "Infinity" ? (
                    <>
                      {change !== 0 ? (
                        <p
                          className={twMerge(
                            change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : "",
                            'flex items-baseline text-xs gap-x-0.5 sm:text-sm'
                          )}
                        >
                          {change > 0 ? (
                            <ArrowUpIcon className="size-4 flex-shrink-0 self-center text-green-500 font-semibold" aria-hidden="true" />
                          ) : change < 0 ? (
                            <ArrowDownIcon className="size-4 flex-shrink-0 self-center text-red-500 font-semibold" aria-hidden="true" />
                          ) : <></>}
                          <span className="sr-only"> {change > 0 ? 'Increased' : 'Decreased'} by </span>
                          {Math.abs(Math.floor(change))}% vs {getGrowthRateText()}
                        </p>
                      ) : <></>}
                    </>
                  ): (
                    <p className="text-sm text-green-600 flex items-baseline gap-x-0.5">
                      <ArrowUpIcon className="h-4 w-4 flex-shrink-0 self-center text-green-500 font-semibold" aria-hidden="true" />
                      ∞ vs {getGrowthRateText()}
                    </p>
                  )}
                </React.Fragment>
              ) : <></>}
              {redirectUrl ? (
                <Link to={redirectUrl} className="w-fit">
                  <p className="w-fit text-sm font-medium text-brand-500 hover:text-brand-400">View more</p>
                </Link>
              ) : <></>}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}